import {createApi} from '@reduxjs/toolkit/dist/query/react';
import {axiosBaseQuery, REFETCH_POLICY} from 'src/axiosBaseQuery';
import {UpdateRfidRequestForDedicatedPage} from 'src/types';
import {
    CreateRfidRequest,
    LinkRfidRequest,
    PageTagsDTO,
    TagByIdRequest,
    TagDeleteRequest,
    TagsDTO,
    TagsSearchRequest,
    ToggleTagStatusRequest,
    DeactivateTagRequest,
} from 'src/types/tags';
import {TagStats} from 'src/types/stats';
import {getBaseURL} from 'src/utils/helper';

export const tagsApi = createApi({
    reducerPath: 'tagsApi',
    ...REFETCH_POLICY,
    baseQuery: axiosBaseQuery({
        baseUrl: String(getBaseURL() + '/tags'),
    }),
    tagTypes: ['tags', 'tag'],
    endpoints: (build) => ({
        getTags: build.query<PageTagsDTO, TagsSearchRequest>({
            query: ({data, params}) => ({url: '/search', method: 'post', data, params}),
            providesTags: ['tags'],
        }),
        // The same body the list is fetched with, so the block counts exactly the set the list
        // shows — the server drops the cells' own filters and forces the tenant.
        getTagStats: build.query<TagStats, TagsSearchRequest['data']>({
            query: (data) => ({url: '/stats/summary', method: 'post', data}),
            providesTags: ['tags'],
        }),
        getTagById: build.query<TagsDTO, TagByIdRequest>({
            query: ({tagId, params}) => ({url: `/${tagId}`, method: 'get', params}),
            providesTags: (_result, _error, arg) => [{type: 'tag' as const, id: arg.tagId}],
        }),
        deleteTag: build.mutation<void, TagDeleteRequest>({
            query: ({tagId, params}) => ({url: `/${tagId}`, method: 'delete', params}),
            invalidatesTags: ['tags'],
        }),
        toggleTagStatus: build.mutation<void, ToggleTagStatusRequest>({
            query: ({tagId, params}) => ({url: `/${tagId}/toggle`, method: 'post', params}),
            invalidatesTags: ['tags'],
        }),
        // One way: the holder's own switch, under rfidTags.deactivate. The toggle above also switches a
        // card back on and needs rfidTags.update, which a driver or an employee does not hold.
        deactivateTag: build.mutation<void, DeactivateTagRequest>({
            query: ({tagId, params}) => ({url: `/${tagId}/deactivate`, method: 'post', params}),
            invalidatesTags: ['tags'],
        }),
        createNewTag: build.mutation<TagsDTO, CreateRfidRequest>({
            query: ({data}) => ({url: '', method: 'post', data: data}),
            invalidatesTags: ['tags'],
        }),
        updateTag: build.mutation<unknown, UpdateRfidRequestForDedicatedPage>({
            query: ({data, tagId}) => ({url: `/${tagId}`, method: 'put', data: data}),
            invalidatesTags: ['tags'],
        }),
        linkTagToUser: build.mutation<unknown, LinkRfidRequest>({
            query: ({tagId, userId, params}) => ({
                url: `/${tagId}/users/${userId}/link`,
                method: 'post',
                params,
            }),
            invalidatesTags: ['tags'],
        }),
        unlinkTagFromUser: build.mutation<unknown, LinkRfidRequest>({
            query: ({tagId, userId, params}) => ({
                url: `/${tagId}/users/${userId}/unlink`,
                method: 'post',
                params,
            }),
            invalidatesTags: ['tags'],
        }),
    }),
});
